import React, { useState } from 'react'
import { STORAGE_KEYS } from '../config/apiConfig'
import { generateKey } from '../services/adminApi'
import CopyButton from '../components/CopyButton'
import Toast from '../components/Toast'
import Spinner from '../components/Spinner'

export default function Admin() {
  const [adminId, setAdminId] = useState(localStorage.getItem(STORAGE_KEYS.adminKey) || '')
  const [adminSecret, setAdminSecret] = useState(localStorage.getItem(STORAGE_KEYS.adminSecret) || '')
  const [generatedKey, setGeneratedKey] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState('')

  const onGenerate = async (e) => {
    e.preventDefault()
    setError('')
    setGeneratedKey('')

    if (!adminId || !adminSecret) {
      setError('Please enter admin credentials.')
      return
    }

    // Persist admin creds for header injection
    localStorage.setItem(STORAGE_KEYS.adminKey, adminId)
    localStorage.setItem(STORAGE_KEYS.adminSecret, adminSecret)

    setBusy(true)
    try {
      const res = await generateKey({ role: 'researcher', expiresAt: null })
      // Try common response shapes
      const key =
        res?.key || res?.api_key || res?.plain || res?.id || ''

      if (!key) {
        setError('Key generated but response did not include a plaintext key.')
      } else {
        setGeneratedKey(key)
        setToast('Researcher key generated')
      }
    } catch (err) {
      setError(err?.message || 'Failed to generate key.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="space-y-6">
      <h1 className="text-xl font-semibold">Admin — Generate Researcher Key</h1>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      <form onSubmit={onGenerate} className="grid gap-4 max-w-md">
        <div className="grid gap-1">
          <label className="text-sm text-gray-700">Admin ID</label>
          <input
            className="h-10 rounded border px-3 outline-none focus:ring-2 focus:ring-gray-400"
            value={adminId}
            onChange={(e) => setAdminId(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <div className="grid gap-1">
          <label className="text-sm text-gray-700">Admin Secret</label>
          <input
            className="h-10 rounded border px-3 outline-none focus:ring-2 focus:ring-gray-400"
            type="password"
            value={adminSecret}
            onChange={(e) => setAdminSecret(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            type="submit"
            className="h-10 rounded bg-gray-900 px-4 text-white hover:bg-black disabled:opacity-50"
            disabled={busy}
          >
            {busy ? <span className="inline-flex items-center gap-2"><Spinner size={4} /> Generating…</span> : 'Generate Key'}
          </button>
        </div>
      </form>

      {generatedKey && (
        <div className="rounded-md border bg-white p-4">
          <div className="text-sm text-gray-600 mb-1">Generated key</div>
          <div className="flex items-center gap-2">
            <code className="rounded bg-gray-100 px-2 py-1 text-sm">{generatedKey}</code>
            <CopyButton text={generatedKey} small />
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Save this now. It won’t be shown again.
          </p>
        </div>
      )}

      <Toast message={toast} type="success" onClose={() => setToast('')} />
    </section>
  )
}
